import {makeStyles} from '@material-ui/styles';
import React, { useContext, useEffect, useState } from "react";
import { Button, Grid } from "@material-ui/core";
import Slick from "react-slick";
import Link from "next/link";
import Image from "next/image";
import TextContent from "../../cms/blocks/text/text-content";
import urlNormalizer from "tools/urlNormalization";
import { useRouter } from "next/router";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import theme from "../../theme";
import { useTranslation } from "tools/i18n";
import { TrackingContext } from "../../providers/tracking";
import { getProductFamily } from "../../../functions/productOps";
import useSessionStore from '@services/stores/SessionStore';

const useStyles = makeStyles(theme => ({
	wrapper: {
		position: 'relative',
		marginTop: 40,
		'& .slick-slider': {
			width: '100%',
			padding: '0',
			overflow: 'hidden',
			[theme.breakpoints.down('sm')]: {
				padding: '0 20px'
			}
		},
		'& .slick-track': {
			display: 'flex',
			gap: 20,
			transform: 'translate3d(0,0,0)'
		},
		'& .slick-slide': {
			display: 'flex',
			'& > div': {
				width: '100%'
			}
		},
		'& .slick-list': {
			marginBottom: 25,
			maxWidth: '100vw',
			transform: 'translate3d(0,0,0)'
		},
		[theme.breakpoints.down('sm')]: {
			margin: '40px 0',
			'& .slick-track': {
				gap: 0
			},
			'& .slick-slider': {
				paddingBottom: 12
			},
			'& .slick-slide': {
				padding: '0 10px',
			},
			'& .slick-list': {
				marginBottom: 30
			}
		},
	},
	title: {
		...theme.typography.h2,
        fontFamily: 'Chronicle, "Open Sans", sans-serif',
		marginBottom: 10,
		textAlign: 'center',
        [theme.breakpoints.down('md')]: {
            fontSize: 20
        }
	},
	imageWrapper: {
		position: 'relative',
		background: theme.palette.primary.color,
		display: 'flex',
		width: '100%',
		maxHeight: 280,
		alignItems: 'center',
		overflow: 'hidden'
	},
	imageInnerWrapper: {
		width: '100%'
	},
	content: {
		padding: '25px 15px 5px',
		maxWidth: 450,
		textAlign: 'center',
		backgroundColor: theme.palette.primary.background,
		'& p, & div': {
			margin: 0,
			fontSize: '16px !important',
			lineHeight: '1.3 !important'
		}
	},
	textContent: {
		position: 'relative',
		display: 'flex',
		maxHeight: 60,
		color: "#444",
		fontWeight: 300,
		overflow: 'hidden',
		'&:before': {
			content: '""',
			position: 'absolute',
			bottom: 0,
			left: 0,
			right: 0,
			height: 0,
			backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))'
		}
	},
	preTitle: {
		color: '#999',
    	margin: 0,
    	fontSize: 13,
    	fontWeight: 300,
    	letterSpacing: 2,
    	textTransform: 'uppercase'
	},
	name: {
		margin: 0,
		fontWeight: 400,
		fontSize: 22,
		fontFamily: '"Times New Roman", serif',
		color: '#222',
		lineHeight: 1.43
	},
	readMore: {
		padding: '8px 20px',
		textTransform: 'none',
		fontWeight: 600,
		borderRadius: 0
	},
	sliderItem: {
		display: 'flex',
		width: '100%'
	},
	sliderItemLink: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		width: '100%',
		color: 'inherit',
		textDecoration: 'none'
	},
	arrow: {
		position: 'absolute',
		bottom: 0,
		zIndex: 10,
		minWidth: 0,
		backgroundColor: theme.palette.primary.background,
		boxShadow: '3px 3px 3px rgba(50, 50, 93, 0.04)'
	},
	arrowRight: {
		right: 0,
	},
	arrowLeft: {
		left: 0,
	},
	arrowIcon: {
		display: 'block'
	},
	slickDots: {
		display: 'flex !important',
		justifyContent: 'center',
		padding: 0,
		listStyle: 'none',
		'& li': {
			width: 5,
			height: 5,
			margin: '2px 4px',
			borderRadius: '50%',
			backgroundColor: 'rgba(0,0,0,0.25)',
			'&.slick-active': {
				width: 6,
				height: 6,
				backgroundColor: 'rgba(0,0,0,0.75)'
			},
			'& button': {
				display: 'none'
			}
		},
		[theme.breakpoints.down('sm')]: {
			margin: 0
		}
	}
}), {name: 'RelatedContent'});

export default function RelatedContent({product, productFamilyArticle}) {
	const router = useRouter();
	const classes = useStyles();
	const {t} = useTranslation(['general']);
	const { token, salesChannel } = useSessionStore((state) => state.session);
	const {actions: trackingActions} = useContext(TrackingContext);
    const [articles, setArticles] = useState([]);

	const settings = {
		dots: true,
		infinite: true,
		autoPlay: false,
		slidesToShow: 3,
		slidesToScroll: 1,
		nextArrow: <Button classes={{root: [classes.arrow, classes.arrowRight].join(' ')}}><ChevronRight classes={{root: classes.arrowIcon}}/></Button>,
		prevArrow: <Button classes={{root: [classes.arrow, classes.arrowLeft].join(' ')}}><ChevronLeft classes={{root: classes.arrowIcon}}/></Button>,
		dotsClass: classes.slickDots,
		responsive: [
			{
				breakpoint: 999999999,
				settings: {
					slidesToShow: 3,
					slidesToScroll: 1,
					infinite: false,
					dots: true
				}
			},
			{
				breakpoint: theme.breakpoints.values.md,
				settings: {
					slidesToShow: 2,
					slidesToScroll: 1,
					infinite: true,
					dots: true
				}
			},
			{
				breakpoint: theme.breakpoints.values.sm,
				settings: {
					slidesToShow: 1,
					slidesToScroll: 1,
					infinite: true,
					dots: true
				}
			}
		]
	};

    useEffect(async () => {
		let categoryIds = [];
		let priority = salesChannel?.translated?.customFields?.related_content_prio;
		if(priority) {
			categoryIds = categoryIds.concat(salesChannel?.translated?.customFields?.related_content_prio.split(','));
		}

		if(product.seoCategory?.translated?.customFields?.related_content) {
			categoryIds = categoryIds.concat(product.seoCategory?.translated?.customFields?.related_content.split(','));
		}

		if(salesChannel?.translated?.customFields?.related_content_fallback) {
			categoryIds = categoryIds.concat(salesChannel?.translated?.customFields?.related_content_fallback.split(','));
		}

		let joinedIds = categoryIds.join(',');

		let productOverride = getMainContentProperty();
		if(productOverride && productOverride.length > 0 && productOverride[0].name) {
			joinedIds = joinedIds + '?productOverride=' + encodeURIComponent(productOverride[0].name);
		}

		if(token && categoryIds.length > 1) {
			let response = await fetch('/api/product/get/related-content/' + joinedIds, {
				headers: {
					'Content-Type': 'application/json'
				}
			});

			if (response.status === 200) {
				let responseJson = await response.json();

				if (responseJson.articles) {
					let sortedArticles = sortArticles(product, responseJson.articles, priority, productOverride);

					if(productFamilyArticle) {
						sortedArticles = sortedArticles.filter((article) => article.id !== productFamilyArticle.id);
						sortedArticles.unshift(productFamilyArticle);
						sortedArticles = sortedArticles.slice(0, 3);
					}

					setArticles(sortedArticles);
				}
			}
		}
    }, [token, product]);

	function sortArticles(product, articles, priority, productOverride) {
		if(priority || productOverride) {
			return articles.reverse().sort((a, b) => {
				// If a product has a product family
				if(getProductFamily(product)) {
					if (a.translated?.customFields.product_family && a.translated?.customFields.product_family.toLowerCase() === getProductFamily(product).toLowerCase()) {
						return -1;
					} else if (b.translated?.customFields.product_family && b.translated?.customFields.product_family.toLowerCase() === getProductFamily(product).toLowerCase()) {
						return 1;
					}
				}

				// if a is prio
				if(priority && a.id === priority) {
					return -1;
				}

				// if b is prio
				if(priority && b.id === priority) {
					return 1;
				}

				// if a is product override
				if(productOverride?.length > 0 && a.seoUrls[0].seoPathInfo === productOverride[0].name && b.id !== priority) {
					return -1;
				}

				return 0;
			});
		}

		return articles
	}

	function getMainContentProperty() {
		return product.properties?.filter((property) => {
			if(property.group?.translated?.customFields?.attributeCode === "main_content") {
				return true;
			}

			return false;
		});
	}

	function getUrl(category) {
		let url = category.seoUrls.length > 0 ? '/' + category.seoUrls[0].seoPathInfo : false;

		if(!url) {
			url = '/navigation/' + category.id;
		}

		return url;
	}

	function urlNormalize(url) {
		return urlNormalizer(url, router.basePath);
	}

	function getImage(article) {
		let media = article?.translated?.customFields?.inspiration_home_image;
		const alt = article.metaTitle;
		if(!media) {
			return {
				src: ' ',
				alt,
				width: 0,
				height: 0
			}
		}

		let returnObject = {
			src: media.url,
			alt,
		};

		returnObject.layout = 'responsive';
		returnObject.width = media.metaData.width;
		returnObject.height = media.metaData.height;

		return returnObject;
	}

	function cutDescription(description = '') {
		let splitByWords = [];
		let words = description?.split(' ');
		let totalLength = 0;

		if(!words) {
			return '';
		}

		for (let word of words) {
			let currentText = splitByWords.join(' ');
			totalLength = currentText.length + word.length;
			if(totalLength <= 90) {
				splitByWords.push(word);
			} else {
				break;
			}
		}

		return splitByWords.join(' ') + '...';
	}

	function getDescription(article) {
		let description = article.translated.description;

		if(article?.customFields?.product_family_description) {
			description = article?.customFields?.product_family_description;
		}

		return cutDescription(description);
	}

	return (
		articles.length > 1 ?
		<div className={classes.wrapper}>
			<Grid xs={12} item>
				<Grid container>
					<Slick {...settings}>
						{articles.map((article, index) => {
							return (
								<div className={classes.sliderItem} key={index}>
									<Link href={urlNormalize(getUrl(article))}>
										<a className={classes.sliderItemLink} onClick={() => {
											trackingActions.click({
												type: 'product_related_link',
												text: article.translated.name,
												identifier: urlNormalize(getUrl(article))
											});
										}}>
											<div className={classes.imageWrapper}>
												<div className={classes.imageInnerWrapper}>
													<Image {...getImage(article)} className={[classes.image, 'image'].join(' ')} loading={'eager'} unoptimized/>
												</div>
											</div>
											<div className={classes.content}>
												<span className={classes.preTitle}>{t('inspiration')}</span>
												<h4 className={classes.name}>{article.translated.customFields.inspiration_home_title || article.translated.name}</h4>
												<TextContent content={getDescription(article)} className={classes.textContent}/>
											</div>
											<Button className={classes.readMore}>{t('read-more')} »</Button>
										</a>
									</Link>
								</div>
							)
						})}
					</Slick>
				</Grid>
			</Grid>
		</div> : ''
	);
}
